import React, { Component, FormEvent, Fragment } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import DateSelector, { DateSelectorProps } from "Shared/Form/DateSelector";
import FieldError from "Shared/Form/FieldError";
import Input from "Shared/Form/Input";
import PhoneInput from "Shared/Form/Input/PhoneInput";
import Label from "Shared/Form/Label";
import { phone, required } from "Shared/Form/validators";
import { CircleRight } from "Shared/Icons";
import styled from "styled-components";
import FormBody from "src/components/ContinueDeal/CreditApp/shared/FormBody";
import FormContainer from "src/components/ContinueDeal/CreditApp/shared/FormContainer";
import FormHeader from "src/components/ContinueDeal/CreditApp/shared/FormHeader";
import * as variables from "src/styles/variables.scss";
import StepContent from "../../StepContent/StepContent";
import { BaseStepProps } from "../shared/BaseStepProps";

const FormGroup = styled("div")`
    margin-bottom: 20px;

    input,
    label {
        display: block;
    }
`;

const EmploymentInformationBody = styled(FormBody)`
    margin: 15px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 28px 68px 0 41px;
    }

    .EmploymentInfo {
        &__employer {
            margin-bottom: 10px;
        }

        &__phone-date-wrapper {
            ${(props) => props.theme.breakpoints.up("desktop")} {
                display: flex;
                justify-content: space-between;
                margin-bottom: 10px;
            }
        }

        &__prev-employer-note {
            font-weight: 500;
            margin-bottom: 30px;

            &-icon {
                color: ${(props) => props.theme.palette.success.main};
                height: 20px;
                margin-right: 8px;
                vertical-align: middle;
                width: 20px;
            }

            &-text {
                vertical-align: middle;
            }
        }
    }
`;

const StepContentContainer = styled("div")`
    margin: auto;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin: 50px 22px 0;
    }
`;

const NameSmallText = styled("span")`
    color: ${variables.colorDark};
    display: flex;
    font-size: 12px;
    font-style: italic;
    font-weight: 300;
    justify-content: flex-end;
    margin-top: 3px;
    text-align: right;
`;

const Phone = styled(FormGroup)`
    width: 100%;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        padding-right: 2%;
        width: 32%;
    }
`;

const StartDate = styled(FormGroup)`
    width: 100;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        width: 66%;

        .month-year-wrapper {
            display: flex;
            justify-content: space-between;

            input {
                width: 48.5%;
            }
        }
    }
`;

export interface EmploymentInfoFormData {
    currentEmployerName: string;
    startDate: Date | null;
    jobTitle: string;
    previousEmployerName: string;
    primaryWorkPhone: string;
}

interface EmploymentInformationProps extends BaseStepProps {
    onNextClick: (data: EmploymentInfoFormData) => any;
}

interface EmploymentInformationState {
    showPreviousEmployer: boolean;
    formData: EmploymentInfoFormData;
    errors: {
        [index: string]: string;
    };
}

class EmploymentInformation extends Component<
    EmploymentInformationProps & WithTranslation,
    EmploymentInformationState
> {
    constructor(props: EmploymentInformationProps & WithTranslation) {
        super(props);
        const formData = props.formData;
        this.state = {
            errors: {},
            formData: {
                currentEmployerName: (formData && formData.currentEmployerName) || "",
                jobTitle: (formData && formData.jobTitle) || "",
                previousEmployerName: (formData && formData.previousEmployerName) || "",
                primaryWorkPhone: (formData && formData.primaryWorkPhone) || "",
                startDate: formData && formData.startDate,
            },
            showPreviousEmployer: false,
        };
    }

    public render() {
        const { isCosigner = false, t } = this.props;
        const { showPreviousEmployer } = this.state;

        return (
            <FormContainer className="EmploymentInfo__container">
                <FormHeader className="EmploymentInfo__header">
                    {isCosigner
                        ? t("ContinueDeal.CreditApp.EmploymentInformation.cosignerTitle")
                        : t("ContinueDeal.CreditApp.EmploymentInformation.title")}
                </FormHeader>
                <EmploymentInformationBody>
                    <form>
                        {this.renderEmployer()}
                        {this.renderJob()}
                        {this.renderPhoneDateWrapper()}
                        {showPreviousEmployer && this.renderPreviousEmployer()}
                    </form>
                </EmploymentInformationBody>
                <StepContentContainer>
                    <StepContent onBackClick={this.handleBackClick} onNextClick={this.handleNextClick} />
                </StepContentContainer>
            </FormContainer>
        );
    }

    public isLessThanTwoYears = (today: Date, date: Date) => {
        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth();
        return todayYear - dateYear < 2 || (todayYear - dateYear === 2 && todayMonth - dateMonth < 0);
    };

    private renderEmployer = () => {
        const { t } = this.props;
        const { errors, formData } = this.state;
        const error: string = errors.currentEmployerName;
        return (
            <FormGroup className="EmploymentInfo__employer">
                <Label htmlFor="currentEmployerName">
                    {t("ContinueDeal.CreditApp.EmploymentInformation.employerLabel")}*
                </Label>
                <Input
                    {...{
                        error: !!error,
                        hasError: !!error,
                        hasLabel: true,
                        id: "currentEmployerName",
                        name: "currentEmployerName",
                        onBlur: this.handleInputBlur,
                        onChange: this.handleInputChange,
                        value: formData.currentEmployerName,
                    }}
                />
                <FieldError>{error}</FieldError>
                <NameSmallText>{t("ContinueDeal.CreditApp.EmploymentInformation.employerHint")}</NameSmallText>
            </FormGroup>
        );
    };

    private renderJob = () => {
        const { t } = this.props;
        const { errors, formData } = this.state;
        const error: string = errors.jobTitle;
        return (
            <FormGroup className="EmploymentInfo__job-title">
                <Label htmlFor="jobTitle">{t("ContinueDeal.CreditApp.EmploymentInformation.jobTitleLabel")}*</Label>
                <Input
                    {...{
                        error: !!error,
                        hasError: !!error,
                        hasLabel: true,
                        id: "jobTitle",
                        name: "jobTitle",
                        onBlur: this.handleInputBlur,
                        onChange: this.handleInputChange,
                        value: formData.jobTitle,
                    }}
                />
                <FieldError>{error}</FieldError>
            </FormGroup>
        );
    };

    private renderPhoneDateWrapper = () => {
        const { t } = this.props;
        const { errors, formData } = this.state;
        const phoneError: string = errors.primaryWorkPhone;
        const startDateError: string = errors.startDate;

        const dateSelectorProps = {
            error: !!startDateError,
            hasError: !!startDateError,
            hasLabel: true,
            name: "startDate",
            onBlur: this.handleInputBlur,
            onChange: this.checkDuration,
            rangeEnd: new Date(),
            rangeStart: new Date(1920, 1, 1),
            type: "MY",
        } as DateSelectorProps;

        if (formData.startDate) {
            dateSelectorProps.value = formData.startDate;
        }

        return (
            <FormGroup className="EmploymentInfo__phone-date-wrapper">
                <Phone className="EmploymentInfo__work-phone">
                    <Label htmlFor="primaryWorkPhone">
                        {t("ContinueDeal.CreditApp.EmploymentInformation.primaryPhoneLabel")}*
                    </Label>
                    <PhoneInput
                        id="primaryWorkPhone"
                        name="primaryWorkPhone"
                        onBlur={this.handleInputBlur}
                        onChange={this.handleInputChange}
                        error={!!phoneError}
                        hasError={!!phoneError}
                        hasLabel={true}
                        value={formData.primaryWorkPhone}
                    />
                    <FieldError>{phoneError}</FieldError>
                </Phone>
                <StartDate className="EmploymentInfo__start-date">
                    <Label>{t("ContinueDeal.CreditApp.EmploymentInformation.startDateLabel")}*</Label>
                    <DateSelector {...dateSelectorProps} />
                    <FieldError>{startDateError}</FieldError>
                </StartDate>
            </FormGroup>
        );
    };

    private renderPreviousEmployer = () => {
        const { t } = this.props;
        const { errors, formData } = this.state;
        const error: string = errors.previousEmployerName;
        return (
            <Fragment>
                <div className="EmploymentInfo__prev-employer-note">
                    <CircleRight className="EmploymentInfo__prev-employer-note-icon" />
                    <span className="EmploymentInfo__prev-employer-note-text">
                        {t("ContinueDeal.CreditApp.EmploymentInformation.previousEmployerMessage")}
                    </span>
                </div>
                <FormGroup className="EmploymentInfo__prev-employer">
                    <Label htmlFor="previousEmployerName">
                        {t("ContinueDeal.CreditApp.EmploymentInformation.previousEmployerNameLabel")}*
                    </Label>
                    <Input
                        {...{
                            error: !!error,
                            hasError: !!error,
                            hasLabel: true,
                            id: "previousEmployerName",
                            name: "previousEmployerName",
                            onBlur: this.handleInputBlur,
                            onChange: this.handleInputChange,
                            value: formData.previousEmployerName,
                        }}
                    />
                    <FieldError>{error}</FieldError>
                </FormGroup>
            </Fragment>
        );
    };

    private handleInputChange = (event: FormEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        const value: string = target.value;
        const name: string = target.name;
        this.setState({
            formData: { ...this.state.formData, [name]: value },
        } as any);
    };

    private checkDuration = (selected: { target: { name: string; value: Date } }) => {
        const date = selected.target.value;

        this.setState({
            formData: { ...this.state.formData, startDate: date },
            showPreviousEmployer: this.isLessThanTwoYears(new Date(), date),
        });
    };

    private handleNextClick = () => {
        if (this.isValid()) {
            this.props.onNextClick(this.state.formData);
        }
    };
    private handleBackClick = () => {
        this.props.onBackClick(this.state.formData);
    };

    private handleInputBlur = (event: FormEvent<HTMLInputElement>) => {
        const target = event.target as HTMLInputElement;
        const value: string = target.value;
        const name: string = target.name;

        if (name === "primaryWorkPhone") {
            this.setState({ errors: { primaryWorkPhone: required(value) + phone(value) } });
        } else {
            this.setState({
                errors: {
                    ...this.state.errors,
                    [name]: required(value),
                },
            });
        }
    };

    private isValid = (): boolean => {
        const { formData, showPreviousEmployer } = this.state;
        const errors: { [index: string]: string } = {};
        errors.currentEmployerName = required(formData.currentEmployerName);
        errors.jobTitle = required(formData.jobTitle);
        errors.primaryWorkPhone = required(formData.primaryWorkPhone) + phone(formData.primaryWorkPhone);
        errors.startDate = required(formData.startDate);

        if (showPreviousEmployer) {
            errors.previousEmployerName = required(formData.previousEmployerName);
        }
        this.setState({ errors });

        for (const key in errors) {
            if (errors[key]) {
                return false;
            }
        }
        return true;
    };
}

export default withTranslation()(EmploymentInformation);
